/* Pure helpers behind the internal utility apps — string transforms, parsers,
   converters and small calculators. No React, no stores, no side effects. */

export type OptValues = Record<string, string | number | boolean>;

/* ── Case & text ──────────────────────────────────────────────────────────── */

export function words(input: string): string[] {
  return input
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
}

const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();

export const toTitleCase = (s: string) =>
  s.replace(/\S+/g, (w) => cap(w));
export const toSentenceCase = (s: string) =>
  s
    .toLowerCase()
    .replace(/(^\s*[a-z])|([.!?]\s+[a-z])/g, (m) => m.toUpperCase());
export const toCamelCase = (s: string) =>
  words(s)
    .map((w, i) => (i === 0 ? w.toLowerCase() : cap(w)))
    .join("");
export const toPascalCase = (s: string) => words(s).map(cap).join("");
export const toSnakeCase = (s: string) =>
  words(s).map((w) => w.toLowerCase()).join("_");
export const toKebabCase = (s: string) =>
  words(s).map((w) => w.toLowerCase()).join("-");
export const toConstantCase = (s: string) =>
  words(s).map((w) => w.toUpperCase()).join("_");
export const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export function lines(s: string): string[] {
  return s.split(/\r?\n/);
}

export function textStats(s: string) {
  const wordCount = (s.match(/\S+/g) ?? []).length;
  const sentences = (s.match(/[^.!?]+[.!?]+/g) ?? []).length || (s.trim() ? 1 : 0);
  const paragraphs = s.split(/\n\s*\n/).filter((p) => p.trim()).length;
  return {
    words: wordCount,
    chars: s.length,
    charsNoSpace: s.replace(/\s/g, "").length,
    lines: s ? lines(s).length : 0,
    sentences,
    paragraphs,
    readMinutes: Math.max(1, Math.round(wordCount / 200)),
  };
}

export function charFrequency(s: string): string {
  const freq = new Map<string, number>();
  for (const ch of s) freq.set(ch, (freq.get(ch) ?? 0) + 1);
  const rows = [...freq.entries()].sort((a, b) => b[1] - a[1]);
  if (!rows.length) return "";
  return rows
    .map(([ch, n]) => {
      const label = ch === " " ? "␣ (space)" : ch === "\n" ? "⏎ (newline)" : ch === "\t" ? "⇥ (tab)" : ch;
      return `${label}  ×${n}`;
    })
    .join("\n");
}

/* ── Encoding ─────────────────────────────────────────────────────────────── */

export function b64encode(s: string, urlSafe = false): string {
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  const out = btoa(bin);
  return urlSafe ? out.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "") : out;
}

export function b64decode(s: string): string {
  const normalized = s.trim().replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  let bin: string;
  try {
    bin = atob(padded);
  } catch {
    throw new Error("Not valid Base64 input.");
  }
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

const HTML_ENTITIES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};
export const htmlEncode = (s: string) =>
  s.replace(/[&<>"']/g, (c) => HTML_ENTITIES[c]);
export function htmlDecode(s: string): string {
  return s
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(parseInt(d, 10)))
    .replace(/&(amp|lt|gt|quot|apos|nbsp);/g, (_, name: string) =>
      ({ amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " }[name] ?? "")
    );
}

export function hexEncode(s: string): string {
  return [...new TextEncoder().encode(s)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join(" ");
}
export function hexDecode(s: string): string {
  const clean = s.replace(/0x/gi, "").replace(/[^0-9a-f]/gi, "");
  if (clean.length % 2 !== 0) throw new Error("Odd number of hex digits.");
  const bytes = new Uint8Array(clean.length / 2);
  for (let i = 0; i < bytes.length; i++)
    bytes[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return new TextDecoder().decode(bytes);
}

export function binaryEncode(s: string): string {
  return [...new TextEncoder().encode(s)]
    .map((b) => b.toString(2).padStart(8, "0"))
    .join(" ");
}
export function binaryDecode(s: string): string {
  const clean = s.replace(/[^01]/g, "");
  if (clean.length % 8 !== 0) throw new Error("Binary length must be a multiple of 8 bits.");
  const bytes = new Uint8Array(clean.length / 8);
  for (let i = 0; i < bytes.length; i++)
    bytes[i] = parseInt(clean.slice(i * 8, i * 8 + 8), 2);
  return new TextDecoder().decode(bytes);
}

export function caesar(s: string, shift: number): string {
  const n = ((shift % 26) + 26) % 26;
  return s.replace(/[a-z]/gi, (c) => {
    const base = c <= "Z" ? 65 : 97;
    return String.fromCharCode(((c.charCodeAt(0) - base + n) % 26) + base);
  });
}
export const rot13 = (s: string) => caesar(s, 13);

const MORSE: Record<string, string> = {
  a: ".-", b: "-...", c: "-.-.", d: "-..", e: ".", f: "..-.", g: "--.",
  h: "....", i: "..", j: ".---", k: "-.-", l: ".-..", m: "--", n: "-.",
  o: "---", p: ".--.", q: "--.-", r: ".-.", s: "...", t: "-", u: "..-",
  v: "...-", w: ".--", x: "-..-", y: "-.--", z: "--..",
  "0": "-----", "1": ".----", "2": "..---", "3": "...--", "4": "....-",
  "5": ".....", "6": "-....", "7": "--...", "8": "---..", "9": "----.",
  ".": ".-.-.-", ",": "--..--", "?": "..--..", "!": "-.-.--", "/": "-..-.",
  "@": ".--.-.", "-": "-....-", "(": "-.--.", ")": "-.--.-", "&": ".-...",
  ":": "---...", ";": "-.-.-.", "=": "-...-", "+": ".-.-.", '"': ".-..-.",
};
const MORSE_REV = Object.fromEntries(Object.entries(MORSE).map(([k, v]) => [v, k]));

export function morseEncode(s: string): string {
  return s
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      [...word].map((c) => MORSE[c] ?? "").filter(Boolean).join(" ")
    )
    .join(" / ");
}
export function morseDecode(s: string): string {
  return s
    .trim()
    .split(/\s*\/\s*/)
    .map((word) =>
      word.split(/\s+/).map((code) => MORSE_REV[code] ?? "�").join("")
    )
    .join(" ");
}

export function unicodeEscape(s: string): string {
  // Iterate UTF-16 code units so astral chars emit surrogate-pair escapes.
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    out += code < 128 ? s[i] : "\\u" + code.toString(16).padStart(4, "0");
  }
  return out;
}
export function unicodeUnescape(s: string): string {
  return s.replace(/\\u\{([0-9a-f]+)\}|\\u([0-9a-f]{4})/gi, (_, braced, plain) =>
    String.fromCharCode(parseInt(braced ?? plain, 16))
  );
}

export function unicodeInspect(s: string): string {
  if (!s) return "";
  return [...s]
    .map((c) => {
      const cp = c.codePointAt(0)!;
      return `${c === "\n" ? "⏎" : c === "\t" ? "⇥" : c}   U+${cp
        .toString(16)
        .toUpperCase()
        .padStart(4, "0")}   dec ${cp}   utf8 ${[...new TextEncoder().encode(c)]
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(" ")}`;
    })
    .join("\n");
}

/* ── Hashing / IDs / random ───────────────────────────────────────────────── */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

export function crc32(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let c = 0xffffffff;
  for (const b of bytes) c = CRC_TABLE[(c ^ b) & 0xff] ^ (c >>> 8);
  return ((c ^ 0xffffffff) >>> 0).toString(16).padStart(8, "0");
}

export async function digest(algo: string, s: string): Promise<string> {
  const buf = await crypto.subtle.digest(algo, new TextEncoder().encode(s));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function hmac(algo: string, key: string, message: string): Promise<string> {
  const k = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(key),
    { name: "HMAC", hash: algo },
    false,
    ["sign"]
  );
  const sig = await crypto.subtle.sign("HMAC", k, new TextEncoder().encode(message));
  return [...new Uint8Array(sig)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function uuidv4(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const B32 = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"; // Crockford
export function ulid(): string {
  let t = Date.now();
  let time = "";
  for (let i = 0; i < 10; i++) {
    time = B32[t % 32] + time;
    t = Math.floor(t / 32);
  }
  const rnd = crypto.getRandomValues(new Uint8Array(16));
  let rand = "";
  for (let i = 0; i < 16; i++) rand += B32[rnd[i] % 32];
  return time + rand;
}

export function nanoid(size = 21): string {
  const alphabet = "useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict";
  const bytes = crypto.getRandomValues(new Uint8Array(size));
  let out = "";
  for (let i = 0; i < size; i++) out += alphabet[bytes[i] & 63];
  return out;
}

export function randomHex(byteLen: number): string {
  const b = crypto.getRandomValues(new Uint8Array(Math.max(1, Math.min(1024, byteLen))));
  return [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

export function randomInt(min: number, max: number): number {
  const lo = Math.ceil(Math.min(min, max));
  const hi = Math.floor(Math.max(min, max));
  const range = hi - lo + 1;
  const r = crypto.getRandomValues(new Uint32Array(1))[0] / 0x100000000;
  return lo + Math.floor(r * range);
}

export function password(len: number, opts: { upper: boolean; digits: boolean; symbols: boolean }): string {
  let pool = "abcdefghijkmnopqrstuvwxyz";
  if (opts.upper) pool += "ABCDEFGHJKLMNPQRSTUVWXYZ";
  if (opts.digits) pool += "23456789";
  if (opts.symbols) pool += "!@#$%^&*()-_=+[]{};:,.<>?";
  const bytes = crypto.getRandomValues(new Uint32Array(len));
  let out = "";
  for (let i = 0; i < len; i++) out += pool[bytes[i] % pool.length];
  return out;
}

const PASSPHRASE_WORDS = [
  "amber", "anchor", "aspen", "atlas", "aurora", "basalt", "beacon", "birch",
  "breeze", "canyon", "cedar", "cinder", "cobalt", "comet", "coral", "crater",
  "delta", "drift", "ember", "falcon", "fjord", "flint", "forest", "garnet",
  "glacier", "granite", "grove", "harbor", "hazel", "horizon", "indigo", "iris",
  "jasper", "juniper", "lagoon", "lantern", "lunar", "maple", "meadow", "mesa",
  "mistral", "nebula", "north", "oasis", "obsidian", "onyx", "orchid", "osprey",
  "pebble", "pine", "prairie", "quartz", "raven", "reef", "ridge", "river",
  "saffron", "sage", "sierra", "slate", "solar", "sparrow", "summit", "sunset",
  "thistle", "timber", "tundra", "umber", "vapor", "violet", "walnut", "willow",
  "winter", "wren", "zenith", "zephyr",
];
export function passphrase(count: number, separator = "-"): string {
  const idx = crypto.getRandomValues(new Uint32Array(count));
  return [...idx].map((i) => PASSPHRASE_WORDS[i % PASSPHRASE_WORDS.length]).join(separator);
}

const LOREM_SENTENCES = [
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
  "Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.",
  "Nisi ut aliquip ex ea commodo consequat.",
  "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum.",
  "Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia.",
  "At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis.",
  "Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil.",
  "Temporibus autem quibusdam et aut officiis debitis aut rerum necessitatibus.",
  "Itaque earum rerum hic tenetur a sapiente delectus, ut aut reiciendis.",
];
export function loremIpsum(paragraphs: number): string {
  const out: string[] = [];
  for (let p = 0; p < paragraphs; p++) {
    const n = 4 + ((p * 3) % 4);
    const sents: string[] = [];
    for (let i = 0; i < n; i++) sents.push(LOREM_SENTENCES[(p * 5 + i * 3) % LOREM_SENTENCES.length]);
    out.push(sents.join(" "));
  }
  return out.join("\n\n");
}

const FIRST_NAMES = ["Ava", "Noah", "Mia", "Liam", "Zoe", "Ethan", "Ivy", "Lucas", "Nora", "Owen", "Priya", "Marcus", "Elena", "Kai", "Sofia", "Diego", "Amara", "Felix", "Yuki", "Omar"];
const LAST_NAMES = ["Reyes", "Chen", "Patel", "Novak", "Kim", "Okafor", "Silva", "Weber", "Haddad", "Nakamura", "Kowalski", "Ferrari", "Andersson", "Dubois", "Moretti", "Ivanov", "Garcia", "Tanaka", "Osei", "Lindqvist"];
export function fakePerson(): string {
  const f = FIRST_NAMES[randomInt(0, FIRST_NAMES.length - 1)];
  const l = LAST_NAMES[randomInt(0, LAST_NAMES.length - 1)];
  const user = `${f.toLowerCase()}.${l.toLowerCase()}`;
  return `${f} ${l} · ${user}@example.com · +1 (555) ${randomInt(100, 999)}-${randomInt(1000, 9999)}`;
}

/* ── JSON & data ──────────────────────────────────────────────────────────── */

export function parseJson(input: string): unknown {
  try {
    return JSON.parse(input);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    const at = msg.match(/position (\d+)/);
    if (at) {
      const pos = Number(at[1]);
      const before = input.slice(0, pos);
      const line = before.split("\n").length;
      const col = pos - before.lastIndexOf("\n");
      throw new Error(`Invalid JSON at line ${line}, column ${col}: ${msg}`);
    }
    throw new Error(`Invalid JSON: ${msg}`);
  }
}

export function sortKeysDeep(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeysDeep);
  if (v && typeof v === "object") {
    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, val]) => [k, sortKeysDeep(val)])
    );
  }
  return v;
}

export function flattenJson(v: unknown, prefix = ""): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (Array.isArray(v)) {
    v.forEach((item, i) => Object.assign(out, flattenJson(item, prefix ? `${prefix}[${i}]` : `[${i}]`)));
  } else if (v && typeof v === "object") {
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      const key = prefix ? `${prefix}.${k}` : k;
      if (val && typeof val === "object") Object.assign(out, flattenJson(val, key));
      else out[key] = val;
    }
  } else if (prefix) {
    out[prefix] = v;
  }
  return out;
}

export function jsonToYaml(v: unknown, indent = 0): string {
  const pad = "  ".repeat(indent);
  const scalar = (x: unknown): string => {
    if (x === null) return "null";
    if (typeof x === "string") {
      return /^[\w./@ -]*$/.test(x) && x.trim() === x && x !== "" && !/^(true|false|null|[-\d])/.test(x)
        ? x
        : JSON.stringify(x);
    }
    return String(x);
  };
  if (Array.isArray(v)) {
    if (!v.length) return pad + "[]";
    return v
      .map((item) => {
        if (item && typeof item === "object")
          return pad + "-\n" + jsonToYaml(item, indent + 1);
        return pad + "- " + scalar(item);
      })
      .join("\n");
  }
  if (v && typeof v === "object") {
    const entries = Object.entries(v as Record<string, unknown>);
    if (!entries.length) return pad + "{}";
    return entries
      .map(([k, val]) => {
        if (val && typeof val === "object")
          return `${pad}${k}:\n${jsonToYaml(val, indent + 1)}`;
        return `${pad}${k}: ${scalar(val)}`;
      })
      .join("\n");
  }
  return pad + scalar(v);
}

/** Pragmatic YAML subset parser: block maps, block lists, quoted/plain scalars,
 *  flow [] / {} via JSON, comments. Covers everyday config files. */
export function yamlToJson(text: string): unknown {
  const src = lines(text)
    .map((l) => l.replace(/\t/g, "  "))
    .filter((l) => l.trim() !== "" && !/^\s*#/.test(l));
  if (!src.length) return null;

  const scalar = (raw: string): unknown => {
    const s = raw.trim();
    if (s === "" || s === "~" || s === "null") return null;
    if (s === "true") return true;
    if (s === "false") return false;
    if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(s)) return Number(s);
    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'")))
      return s.slice(1, -1);
    if (s.startsWith("[") || s.startsWith("{")) {
      try {
        return JSON.parse(s.replace(/'/g, '"'));
      } catch {
        return s;
      }
    }
    return s.replace(/\s+#.*$/, "");
  };

  let i = 0;
  function parseBlock(minIndent: number): unknown {
    const first = src[i];
    const indent = first.search(/\S/);
    if (indent < minIndent) return null;
    const isList = /^\s*- /.test(first) || /^\s*-$/.test(first);

    if (isList) {
      const arr: unknown[] = [];
      while (i < src.length) {
        const line = src[i];
        const ind = line.search(/\S/);
        if (ind < indent || !/^\s*-( |$)/.test(line)) break;
        const rest = line.replace(/^\s*-\s?/, "");
        i++;
        if (rest === "") {
          arr.push(i < src.length && src[i].search(/\S/) > ind ? parseBlock(ind + 1) : null);
        } else if (/^[^:'"]*:( |$)/.test(rest)) {
          // inline "key: value" opens a map on the same line
          src.splice(i, 0, " ".repeat(ind + 2) + rest);
          arr.push(parseBlock(ind + 1));
        } else {
          arr.push(scalar(rest));
        }
      }
      return arr;
    }

    const obj: Record<string, unknown> = {};
    while (i < src.length) {
      const line = src[i];
      const ind = line.search(/\S/);
      if (ind < indent) break;
      if (ind > indent) throw new Error(`Unexpected indentation at: "${line.trim()}"`);
      const m = line.match(/^\s*("?[^:"]+"?)\s*:\s*(.*)$/);
      if (!m) throw new Error(`Can't parse line: "${line.trim()}"`);
      const key = m[1].replace(/^"|"$/g, "");
      const rest = m[2].replace(/\s+#.*$/, "");
      i++;
      if (rest === "") {
        obj[key] = i < src.length && src[i].search(/\S/) > indent ? parseBlock(indent + 1) : null;
      } else {
        obj[key] = scalar(rest);
      }
    }
    return obj;
  }

  return parseBlock(0);
}

export function csvParse(text: string, delimiter = ","): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  const push = () => {
    row.push(cell);
    cell = "";
  };
  for (let idx = 0; idx < text.length; idx++) {
    const c = text[idx];
    if (inQuotes) {
      if (c === '"') {
        if (text[idx + 1] === '"') {
          cell += '"';
          idx++;
        } else inQuotes = false;
      } else cell += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === delimiter) {
      push();
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[idx + 1] === "\n") idx++;
      push();
      rows.push(row);
      row = [];
    } else {
      cell += c;
    }
  }
  if (cell !== "" || row.length) {
    push();
    rows.push(row);
  }
  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
}

export function csvStringify(rows: string[][], delimiter = ","): string {
  const esc = (s: string) =>
    /["\n\r]/.test(s) || s.includes(delimiter) ? `"${s.replace(/"/g, '""')}"` : s;
  return rows.map((r) => r.map(esc).join(delimiter)).join("\n");
}

export function csvToJson(text: string, delimiter = ",", headerRow = true): string {
  const rows = csvParse(text, delimiter);
  if (!rows.length) throw new Error("No rows found.");
  if (!headerRow) return JSON.stringify(rows, null, 2);
  const [head, ...rest] = rows;
  const objs = rest.map((r) =>
    Object.fromEntries(head.map((h, i) => [h || `col${i + 1}`, r[i] ?? ""]))
  );
  return JSON.stringify(objs, null, 2);
}

export function jsonToCsv(input: string): string {
  const data = parseJson(input);
  if (!Array.isArray(data)) throw new Error("Expected a JSON array of objects.");
  const keys: string[] = [];
  data.forEach((item) => {
    if (item && typeof item === "object" && !Array.isArray(item))
      Object.keys(item).forEach((k) => {
        if (!keys.includes(k)) keys.push(k);
      });
  });
  if (!keys.length) throw new Error("Array items must be objects.");
  const rows = [keys, ...data.map((item) =>
    keys.map((k) => {
      const v = (item as Record<string, unknown>)[k];
      return v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
    })
  )];
  return csvStringify(rows);
}

export function csvToMarkdown(text: string, delimiter = ","): string {
  const rows = csvParse(text, delimiter);
  if (!rows.length) throw new Error("No rows found.");
  const [head, ...rest] = rows;
  const esc = (s: string) => s.replace(/\|/g, "\\|");
  return [
    `| ${head.map(esc).join(" | ")} |`,
    `| ${head.map(() => "---").join(" | ")} |`,
    ...rest.map((r) => `| ${head.map((_, i) => esc(r[i] ?? "")).join(" | ")} |`),
  ].join("\n");
}

export function envToJson(text: string): string {
  const out: Record<string, string> = {};
  for (const raw of lines(text)) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const m = line.replace(/^export\s+/, "").match(/^([\w.]+)\s*=\s*(.*)$/);
    if (!m) continue;
    let v = m[2];
    if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'")))
      v = v.slice(1, -1);
    out[m[1]] = v;
  }
  if (!Object.keys(out).length) throw new Error("No KEY=value pairs found.");
  return JSON.stringify(out, null, 2);
}

export function jwtDecode(token: string): string {
  const parts = token.trim().split(".");
  if (parts.length < 2) throw new Error("A JWT has 3 dot-separated segments (header.payload.signature).");
  const decodePart = (p: string, label: string) => {
    try {
      return JSON.parse(b64decode(p));
    } catch {
      throw new Error(`Couldn't decode ${label} segment.`);
    }
  };
  const header = decodePart(parts[0], "header");
  const payload = decodePart(parts[1], "payload") as Record<string, unknown>;
  const fmt = (k: string) => {
    const v = payload[k];
    return typeof v === "number" ? `  (${k}: ${new Date(v * 1000).toISOString()})` : "";
  };
  const notes = ["exp", "iat", "nbf"].map(fmt).filter(Boolean).join("\n");
  return (
    `── Header ──\n${JSON.stringify(header, null, 2)}\n\n── Payload ──\n${JSON.stringify(payload, null, 2)}` +
    (notes ? `\n\n── Timestamps ──\n${notes}` : "") +
    `\n\n⚠ Signature not verified — this tool only decodes.`
  );
}

export function queryStringParse(input: string): string {
  const q = input.trim().replace(/^[^?]*\?/, "");
  if (!q) throw new Error("No query string found.");
  const params = new URLSearchParams(q);
  const out: Record<string, string | string[]> = {};
  for (const key of new Set(params.keys())) {
    const all = params.getAll(key);
    out[key] = all.length > 1 ? all : all[0];
  }
  return JSON.stringify(out, null, 2);
}

export function urlParse(input: string): string {
  let u: URL;
  try {
    u = new URL(input.trim());
  } catch {
    throw new Error("Not a valid absolute URL (include the protocol, e.g. https://…).");
  }
  const rows = [
    ["protocol", u.protocol],
    ["host", u.host],
    ["hostname", u.hostname],
    ["port", u.port || "(default)"],
    ["pathname", u.pathname],
    ["query", u.search || "(none)"],
    ["hash", u.hash || "(none)"],
    ["origin", u.origin],
  ];
  const params = [...u.searchParams.entries()];
  return (
    rows.map(([k, v]) => `${k.padEnd(9)} ${v}`).join("\n") +
    (params.length
      ? "\n\n── Query params ──\n" + params.map(([k, v]) => `${k} = ${v}`).join("\n")
      : "")
  );
}

export function xmlFormat(input: string): string {
  const compact = input.replace(/>\s+</g, "><").trim();
  if (!compact.startsWith("<")) throw new Error("Input doesn't look like XML.");
  let out = "";
  let indent = 0;
  const tokens = compact.split(/(?=<)|(?<=>)/g).filter(Boolean);
  for (const t of tokens) {
    if (/^<\/[^>]+>$/.test(t)) {
      indent = Math.max(0, indent - 1);
      out += "  ".repeat(indent) + t + "\n";
    } else if (/^<[^>]*\/>$/.test(t) || /^<[?!][^>]*>$/.test(t)) {
      out += "  ".repeat(indent) + t + "\n";
    } else if (/^<[^>]+>$/.test(t)) {
      out += "  ".repeat(indent) + t + "\n";
      indent++;
    } else if (t.trim()) {
      out += "  ".repeat(indent) + t.trim() + "\n";
    }
  }
  return out.trimEnd();
}

/* ── Time ─────────────────────────────────────────────────────────────────── */

export function humanDuration(ms: number): string {
  if (!isFinite(ms)) throw new Error("Not a finite number.");
  const neg = ms < 0;
  let rest = Math.abs(ms);
  const units: [string, number][] = [
    ["d", 86400000],
    ["h", 3600000],
    ["m", 60000],
    ["s", 1000],
    ["ms", 1],
  ];
  const parts: string[] = [];
  for (const [label, size] of units) {
    const n = Math.floor(rest / size);
    if (n > 0 || (label === "ms" && !parts.length)) parts.push(`${n}${label}`);
    rest -= n * size;
    if (parts.length >= 3) break;
  }
  return (neg ? "-" : "") + parts.join(" ");
}

export function relativeTime(from: Date, to: Date): string {
  const diff = to.getTime() - from.getTime();
  const abs = Math.abs(diff);
  const s = humanDuration(abs);
  return diff >= 0 ? `${s} ago` : `in ${s}`;
}

/* Cron: 5-field crontab (minute hour day-of-month month day-of-week). */
interface CronSpec {
  minute: Set<number>;
  hour: Set<number>;
  dom: Set<number>;
  month: Set<number>;
  dow: Set<number>;
  raw: string[];
}

const CRON_RANGES: [number, number][] = [
  [0, 59],
  [0, 23],
  [1, 31],
  [1, 12],
  [0, 6],
];
const MONTH_NAMES = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const CRON_ALIASES: Record<string, string> = {
  "@hourly": "0 * * * *",
  "@daily": "0 0 * * *",
  "@midnight": "0 0 * * *",
  "@weekly": "0 0 * * 0",
  "@monthly": "0 0 1 * *",
  "@yearly": "0 0 1 1 *",
  "@annually": "0 0 1 1 *",
};

export function parseCron(expr: string): CronSpec {
  const normalized = CRON_ALIASES[expr.trim().toLowerCase()] ?? expr.trim();
  const fields = normalized.split(/\s+/);
  if (fields.length !== 5)
    throw new Error("Expected 5 fields: minute hour day-of-month month day-of-week (or @daily-style alias).");
  const name = (f: string) =>
    f
      .replace(/jan/i, "1").replace(/feb/i, "2").replace(/mar/i, "3").replace(/apr/i, "4")
      .replace(/may/i, "5").replace(/jun/i, "6").replace(/jul/i, "7").replace(/aug/i, "8")
      .replace(/sep/i, "9").replace(/oct/i, "10").replace(/nov/i, "11").replace(/dec/i, "12")
      .replace(/sun/i, "0").replace(/mon/i, "1").replace(/tue/i, "2").replace(/wed/i, "3")
      .replace(/thu/i, "4").replace(/fri/i, "5").replace(/sat/i, "6");
  const parseField = (field: string, [lo, hi]: [number, number], label: string): Set<number> => {
    const out = new Set<number>();
    for (const part of name(field).split(",")) {
      const m = part.match(/^(\*|\d+(?:-\d+)?)(?:\/(\d+))?$/);
      if (!m) throw new Error(`Can't parse "${part}" in the ${label} field.`);
      const step = m[2] ? parseInt(m[2], 10) : 1;
      if (step < 1) throw new Error(`Step must be ≥ 1 in the ${label} field.`);
      let start = lo;
      let end = hi;
      if (m[1] !== "*") {
        const [a, b] = m[1].split("-").map((n) => parseInt(n, 10));
        start = a;
        end = b ?? (m[2] ? hi : a);
      }
      if (start < lo || end > hi || start > end)
        throw new Error(`"${part}" is out of range (${lo}–${hi}) in the ${label} field.`);
      for (let v = start; v <= end; v += step) out.add(v === 7 && label === "day-of-week" ? 0 : v);
    }
    return out;
  };
  const raw = fields;
  return {
    minute: parseField(fields[0], CRON_RANGES[0], "minute"),
    hour: parseField(fields[1], CRON_RANGES[1], "hour"),
    dom: parseField(fields[2], CRON_RANGES[2], "day-of-month"),
    month: parseField(fields[3], CRON_RANGES[3], "month"),
    dow: parseField(fields[4].replace(/7/g, "0"), CRON_RANGES[4], "day-of-week"),
    raw,
  };
}

export function describeCron(expr: string): string {
  const spec = parseCron(expr);
  const list = (s: Set<number>, fmt: (n: number) => string = String) =>
    [...s].sort((a, b) => a - b).map(fmt).join(", ");
  const every = (s: Set<number>, [lo, hi]: [number, number]) => s.size === hi - lo + 1;

  const parts: string[] = [];
  if (every(spec.minute, CRON_RANGES[0]) && every(spec.hour, CRON_RANGES[1])) {
    parts.push("Every minute");
  } else if (spec.minute.size === 1 && every(spec.hour, CRON_RANGES[1])) {
    parts.push(`At minute ${list(spec.minute)} of every hour`);
  } else if (every(spec.minute, CRON_RANGES[0])) {
    parts.push(`Every minute during hour(s) ${list(spec.hour)}`);
  } else {
    const hh = every(spec.hour, CRON_RANGES[1]) ? "every hour" : `hour(s) ${list(spec.hour)}`;
    parts.push(`At minute ${list(spec.minute)} past ${hh}`);
  }
  if (!every(spec.dom, CRON_RANGES[2])) parts.push(`on day(s) ${list(spec.dom)} of the month`);
  if (!every(spec.month, CRON_RANGES[3])) parts.push(`in ${list(spec.month, (n) => MONTH_NAMES[n])}`);
  if (!every(spec.dow, CRON_RANGES[4])) parts.push(`on ${list(spec.dow, (n) => DOW_NAMES[n])}`);
  return parts.join(" · ");
}

export function nextCronRuns(expr: string, count = 5, from = new Date()): Date[] {
  const spec = parseCron(expr);
  const out: Date[] = [];
  const t = new Date(from.getTime());
  t.setSeconds(0, 0);
  t.setMinutes(t.getMinutes() + 1);
  const domRestricted = spec.raw[2] !== "*";
  const dowRestricted = spec.raw[4] !== "*";
  // vixie-cron: if both dom and dow are restricted, either may match
  const dayMatches = (d: Date) => {
    const domOk = spec.dom.has(d.getDate());
    const dowOk = spec.dow.has(d.getDay());
    if (domRestricted && dowRestricted) return domOk || dowOk;
    return domOk && dowOk;
  };
  const limit = 366 * 24 * 60 * 2; // scan up to ~2 years of minutes
  for (let i = 0; i < limit && out.length < count; i++) {
    if (
      spec.month.has(t.getMonth() + 1) &&
      dayMatches(t) &&
      spec.hour.has(t.getHours()) &&
      spec.minute.has(t.getMinutes())
    ) {
      out.push(new Date(t.getTime()));
    }
    t.setMinutes(t.getMinutes() + 1);
  }
  return out;
}

/* ── Numbers ──────────────────────────────────────────────────────────────── */

export function convertBase(value: string, from: number, to: number): string {
  const clean = value.trim().toLowerCase().replace(/^0x|^0b|^0o/, "");
  if (!clean) throw new Error("Enter a number to convert.");
  const digits = "0123456789abcdefghijklmnopqrstuvwxyz";
  let n = 0n;
  for (const c of clean) {
    if (c === "_" || c === " ") continue;
    const d = digits.indexOf(c);
    if (d < 0 || d >= from) throw new Error(`"${c}" isn't a valid base-${from} digit.`);
    n = n * BigInt(from) + BigInt(d);
  }
  if (n === 0n) return "0";
  let out = "";
  const base = BigInt(to);
  while (n > 0n) {
    out = digits[Number(n % base)] + out;
    n /= base;
  }
  return out;
}

const ROMAN: [number, string][] = [
  [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"], [100, "C"], [90, "XC"],
  [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
];
export function toRoman(n: number): string {
  if (!Number.isInteger(n) || n < 1 || n > 3999) throw new Error("Enter an integer from 1 to 3999.");
  let out = "";
  for (const [v, sym] of ROMAN)
    while (n >= v) {
      out += sym;
      n -= v;
    }
  return out;
}
export function fromRoman(s: string): number {
  const vals: Record<string, number> = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  const up = s.trim().toUpperCase();
  if (!/^[IVXLCDM]+$/.test(up)) throw new Error("Only Roman numeral characters (I V X L C D M) allowed.");
  let total = 0;
  for (let i = 0; i < up.length; i++) {
    const v = vals[up[i]];
    const next = vals[up[i + 1]] ?? 0;
    total += v < next ? -v : v;
  }
  if (toRoman(total) !== up) throw new Error(`"${up}" isn't a canonical Roman numeral.`);
  return total;
}

const ONES = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
const SCALE = ["", " thousand", " million", " billion", " trillion"];
export function numberToWords(n: number): string {
  if (!Number.isFinite(n)) throw new Error("Not a finite number.");
  if (!Number.isInteger(n)) throw new Error("Whole numbers only.");
  if (Math.abs(n) > 999_999_999_999_999) throw new Error("Number too large (max ~1 quadrillion).");
  if (n === 0) return "zero";
  const three = (x: number): string => {
    const h = Math.floor(x / 100);
    const r = x % 100;
    const tail = r < 20 ? ONES[r] : TENS[Math.floor(r / 10)] + (r % 10 ? "-" + ONES[r % 10] : "");
    return [h ? ONES[h] + " hundred" : "", tail].filter(Boolean).join(" ");
  };
  const neg = n < 0;
  let abs = Math.abs(n);
  const chunks: string[] = [];
  let scale = 0;
  while (abs > 0) {
    const c = abs % 1000;
    if (c) chunks.unshift(three(c) + SCALE[scale]);
    abs = Math.floor(abs / 1000);
    scale++;
  }
  return (neg ? "minus " : "") + chunks.join(", ");
}

export function primeFactors(n: number): number[] {
  if (!Number.isInteger(n) || n < 2) throw new Error("Enter an integer ≥ 2.");
  if (n > 1e12) throw new Error("Max supported value is 10¹².");
  const out: number[] = [];
  let rest = n;
  for (let p = 2; p * p <= rest; p += p === 2 ? 1 : 2) {
    while (rest % p === 0) {
      out.push(p);
      rest /= p;
    }
  }
  if (rest > 1) out.push(rest);
  return out;
}

export function gcd(a: number, b: number): number {
  a = Math.abs(Math.trunc(a));
  b = Math.abs(Math.trunc(b));
  while (b) [a, b] = [b, a % b];
  return a;
}

/* ── Network ──────────────────────────────────────────────────────────────── */

export function parseIpv4(s: string): number {
  const m = s.trim().match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) throw new Error("Not a valid IPv4 address.");
  const parts = m.slice(1).map(Number);
  if (parts.some((p) => p > 255)) throw new Error("Each octet must be 0–255.");
  return ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
}

export function ipv4ToString(n: number): string {
  return [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join(".");
}

export function isPrivateIpv4(n: number): boolean {
  const a = (n >>> 24) & 255;
  const b = (n >>> 16) & 255;
  return (
    a === 10 ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168) ||
    a === 127 ||
    (a === 169 && b === 254)
  );
}

export function cidrInfo(cidr: string) {
  const m = cidr.trim().match(/^(.+)\/(\d{1,2})$/);
  if (!m) throw new Error("Use CIDR notation, e.g. 10.0.0.0/24.");
  const prefix = Number(m[2]);
  if (prefix > 32) throw new Error("Prefix must be 0–32.");
  const ip = parseIpv4(m[1]);
  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  const network = (ip & mask) >>> 0;
  const broadcast = (network | (~mask >>> 0)) >>> 0;
  const total = 2 ** (32 - prefix);
  const usable = prefix >= 31 ? total : Math.max(0, total - 2);
  return {
    network: ipv4ToString(network),
    broadcast: ipv4ToString(broadcast),
    mask: ipv4ToString(mask),
    firstHost: ipv4ToString(prefix >= 31 ? network : network + 1),
    lastHost: ipv4ToString(prefix >= 31 ? broadcast : broadcast - 1),
    total,
    usable,
  };
}

/* ── Colors ───────────────────────────────────────────────────────────────── */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export function parseColor(input: string): RGB {
  const s = input.trim().toLowerCase();
  const hex = s.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = [...h].map((c) => c + c).join("");
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  const rgb = s.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (rgb) {
    const [r, g, b] = rgb.slice(1).map(Number);
    if ([r, g, b].some((v) => v > 255)) throw new Error("RGB channels must be 0–255.");
    return { r, g, b };
  }
  const hsl = s.match(/^hsla?\(\s*([\d.]+)[,\s]+([\d.]+)%[,\s]+([\d.]+)%/);
  if (hsl) {
    const [h, sat, l] = hsl.slice(1).map(Number);
    return hslToRgb(h, sat / 100, l / 100);
  }
  throw new Error("Use #hex, rgb(r, g, b) or hsl(h, s%, l%).");
}

export function rgbToHex({ r, g, b }: RGB): string {
  return "#" + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");
}

export function rgbToHsl({ r, g, b }: RGB): { h: number; s: number; l: number } {
  const rn = r / 255,
    gn = g / 255,
    bn = b / 255;
  const max = Math.max(rn, gn, bn),
    min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h: h * 360, s, l };
}

export function hslToRgb(h: number, s: number, l: number): RGB {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
}

export function luminance({ r, g, b }: RGB): number {
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function contrastRatio(a: RGB, b: RGB): number {
  const l1 = luminance(a);
  const l2 = luminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/* ── Diff ─────────────────────────────────────────────────────────────────── */

export interface DiffLine {
  type: "same" | "add" | "del";
  text: string;
}

export function diffLines(a: string, b: string): DiffLine[] {
  const A = lines(a);
  const B = lines(b);
  // Trim common prefix/suffix so the DP table stays small on big inputs.
  let start = 0;
  while (start < A.length && start < B.length && A[start] === B[start]) start++;
  let endA = A.length;
  let endB = B.length;
  while (endA > start && endB > start && A[endA - 1] === B[endB - 1]) {
    endA--;
    endB--;
  }
  const midA = A.slice(start, endA);
  const midB = B.slice(start, endB);
  if (midA.length * midB.length > 4_000_000)
    throw new Error("Inputs differ too much to diff in the browser (try smaller sections).");

  const n = midA.length;
  const m = midB.length;
  const dp: Uint32Array[] = [];
  for (let i = 0; i <= n; i++) dp.push(new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = midA[i] === midB[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);

  const out: DiffLine[] = A.slice(0, start).map((text) => ({ type: "same", text }));
  let i = 0,
    j = 0;
  while (i < n && j < m) {
    if (midA[i] === midB[j]) {
      out.push({ type: "same", text: midA[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: "del", text: midA[i] });
      i++;
    } else {
      out.push({ type: "add", text: midB[j] });
      j++;
    }
  }
  while (i < n) out.push({ type: "del", text: midA[i++] });
  while (j < m) out.push({ type: "add", text: midB[j++] });
  A.slice(endA).forEach((text) => out.push({ type: "same", text }));
  return out;
}

/* ── Markdown (tiny, sanitized renderer for previews) ─────────────────────── */

export function renderMarkdown(md: string): string {
  const esc = htmlEncode;
  const inline = (s: string): string =>
    esc(s)
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
      .replace(/~~([^~]+)~~/g, "<del>$1</del>")
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      );

  const blocks = md.replace(/\r\n/g, "\n").split(/\n{2,}/);
  const html = blocks.map((block) => {
    const b = block.replace(/\n+$/, "");
    if (!b.trim()) return "";
    const fence = b.match(/^```(\w*)\n?([\s\S]*?)\n?```$/);
    if (fence) return `<pre><code>${esc(fence[2])}</code></pre>`;
    const h = b.match(/^(#{1,6})\s+(.*)$/);
    if (h && !b.includes("\n")) return `<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`;
    if (/^(-{3,}|\*{3,})$/.test(b.trim())) return "<hr/>";
    if (b.split("\n").every((l) => /^\s*>/.test(l)))
      return `<blockquote>${inline(b.replace(/^\s*>\s?/gm, ""))}</blockquote>`;
    if (b.split("\n").every((l) => /^\s*([-*+]|\d+\.)\s/.test(l))) {
      const ordered = /^\s*\d+\./.test(b);
      const items = b
        .split("\n")
        .map((l) => `<li>${inline(l.replace(/^\s*([-*+]|\d+\.)\s/, ""))}</li>`)
        .join("");
      return ordered ? `<ol>${items}</ol>` : `<ul>${items}</ul>`;
    }
    return `<p>${inline(b).replace(/\n/g, "<br/>")}</p>`;
  });
  return html.filter(Boolean).join("\n");
}
